import React from 'react';
import { Container, Typography, Paper, Link } from '@mui/material';
import { styled } from '@mui/system';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    marginTop: theme.spacing(4),
    borderRadius: theme.spacing(2),
    boxShadow: '0 3px 5px 2px rgba(0, 0, 0, .1)',
}));

function TermsOfService() {
    return (
        <Container maxWidth="md" style={{ marginTop: '50px' }}>
            <Typography variant="h4" component="h1" align="center" gutterBottom>
                TERMS OF SERVICE FOR BORROWED, INC. (U.S. & CANADA)
            </Typography>
            <StyledPaper>
                <Typography variant="subtitle1" gutterBottom>
                    <strong>Effective Date: [March 12, 2025]</strong>
                </Typography>
                <Typography paragraph>
                    Welcome to getborrowed.com (the <strong>"Borrowed Site"</strong>), owned and operated by Borrowed, Inc. (<strong>"Borrowed," "we," "our," or "us"</strong>). These Terms of Service (<strong>"Terms"</strong>) govern your access to and use of our platform, including our website, mobile applications, and related software and services (collectively, the <strong>"Borrowed Service"</strong>).
                </Typography>
                <Typography paragraph>
                    <strong>PLEASE READ THESE TERMS CAREFULLY.</strong> BY ACCESSING OR USING THE BORROWED SERVICE, REGISTERING FOR AN ACCOUNT, OR AGREEING TO THESE TERMS VIA A BUTTON OR CHECKBOX, YOU AGREE TO BE BOUND BY THESE TERMS AND OUR PRIVACY POLICY&nbsp;
                    <Link href="https://getborrowed.com/privacy-policy" target="_blank" rel="noopener">
                        [getborrowed.com/privacy-policy]
                    </Link>
                    . IF YOU DO NOT AGREE, DO NOT USE THE SERVICE.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    Acknowledgements
                </Typography>
                <Typography paragraph>
                    You acknowledge and agree:
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Borrowed facilitates, but is not a party to, any Lending Transactions between Owners/Lenders and Borrowers.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Owners/Lenders authorize us and our third-party processors to charge fees for cancellations.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Borrowers authorize us and our third-party processors to charge for lost, stolen, or damaged items.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            We reserve the right to modify these Terms at our discretion and notify you by reasonable means.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            <strong>THESE TERMS INCLUDE A BINDING ARBITRATION AGREEMENT AND CLASS ACTION/JURY TRIAL WAIVER.</strong>
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Additional terms may apply to specific features or services and are incorporated herein by reference.
                        </Typography>
                    </li>
                </ul>

                <Typography variant="h6" component="h2" gutterBottom>
                    1. The Borrowed Service
                </Typography>
                <Typography paragraph>
                    Borrowed is a peer-to-peer platform where users (<strong>"Owners/Lenders"</strong>) can lend items to other users (<strong>"Borrowers"</strong>) in exchange for payment. These transactions are called <strong>"Lending Transactions"</strong>. Borrowed is not responsible for item quality, delivery, or condition, and does not verify an item Owner/Lender.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    2. Eligibility and Account
                </Typography>
                <Typography paragraph>
                    You must be at least 18 years old and capable of entering into a binding contract. Users previously banned or removed by Borrowed may not access the Service.
                </Typography>
                <Typography paragraph>
                    When you create an account, you agree to:
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Provide accurate, complete, and updated information
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Maintain confidentiality of your login credentials
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Notify us of any unauthorized use
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Accept responsibility for all activity on your account
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Connecting a third-party account (e.g. Google, Apple) grants us access to use associated data as permitted.
                        </Typography>
                    </li>
                </ul>

                <Typography variant="h6" component="h2" gutterBottom>
                    3. License and Restrictions
                </Typography>
                <Typography paragraph>
                    We grant you a limited, non-transferable, revocable license to use the Service. You may not:
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Use bots, scrapers, or automated tools to extract data
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Post illegal, harmful, or infringing content
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Harass or abuse users
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Circumvent or interfere with platform features or fees
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Attempt to reverse-engineer our technology
                        </Typography>
                    </li>
                </ul>
                <Typography paragraph>
                    We may suspend or terminate access at any time.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    4. Lending Transactions
                </Typography>
                <Typography paragraph>
                    <strong>For Owners/Lenders:</strong>
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Set prices, availability, and provide accurate item descriptions.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            If you cancel an accepted booking, a 30% cancellation fee applies.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Items must be clean, functional, and lawfully owned.
                        </Typography>
                    </li>
                </ul>
                <Typography paragraph>
                    <strong>For Borrowers:</strong>
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Pay all applicable fees upon booking.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Return items in the same condition on time.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Late returns may incur a daily penalty up to 2x the daily rate.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            You authorize charges for item damage, loss, or failure to return.
                        </Typography>
                    </li>
                </ul>

                <Typography variant="h6" component="h2" gutterBottom>
                    5. Fees and Payments
                </Typography>
                <Typography paragraph>
                    <strong>Rental/Borrow Fee:</strong> Set by Owners/Lenders to the Borrower.
                </Typography>
                <Typography paragraph>
                    <strong>Owner/Lender Fee:</strong> 14% fee from earnings.
                </Typography>
                <Typography paragraph>
                    <strong>Payment Processing:</strong> Via Stripe; see Stripe Services Agreement.
                </Typography>
                <Typography paragraph>
                    <strong>Taxes:</strong> Users are responsible for all applicable taxes and filings.
                </Typography>
                <Typography paragraph>
                    <strong>Commission Avoidance:</strong> Off-platform transactions are prohibited. Violators may be fined up to $200 or have accounts terminated.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    6. Insurance and Owner Protection
                </Typography>
                <Typography paragraph>
                    Borrowed may offer a discretionary Owner Protection Policy for lost, stolen, or damaged items (subject to eligibility and limitations). This is not insurance. Details will be available at&nbsp;
                    <Link href="https://getborrowed.com/owner-protection" target="_blank" rel="noopener">
                        [getborrowed.com/owner-protection]
                    </Link>
                    &nbsp;(link forthcoming).
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    7. Reviews &amp; Ratings
                </Typography>
                <Typography paragraph>
                    Post transactions, Users may leave ratings and written reviews. Reviews must be honest and respectful. Review manipulation is not allowed. Reviews are public and part of your user profile.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    8. User Disputes and Mediation
                </Typography>
                <Typography paragraph>
                    We are not a party to Transactions made on the getborrowed platform. You are responsible for resolving disputes directly. If Borrowed agrees to mediate, we may charge a fee up to 30% of the value recovered.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    9. User Content and Owners/Lenders
                </Typography>
                <Typography paragraph>
                    Users retain rights to content they submit (photos, videos, listings, reviews, etc.), but grant Borrowed a worldwide, royalty-free license to use, promote, and distribute it through our platform and related channels. You agree that:
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            You own the content or have permission to use it.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Content is truthful and lawful.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Borrowed may use your name, image, and likeness associated with the content.
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            We may remove content that violates these Terms.
                        </Typography>
                    </li>
                </ul>

                <Typography variant="h6" component="h2" gutterBottom>
                    10. Prohibited Items
                </Typography>
                <Typography paragraph>
                    You may not list, borrow, or store:
                </Typography>
                <ul>
                    <li>
                        <Typography component="span">
                            Alcohol, tobacco, drugs, or paraphernalia
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Illegal, stolen, or counterfeit items
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Weapons, hazardous materials, or explosives
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Pornography or mature content
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Animals or animal products
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Items in poor condition or that violate IP laws
                        </Typography>
                    </li>
                </ul>
                <Typography paragraph>
                    This list is subject to change.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    11. Privacy and Communications
                </Typography>
                <Typography paragraph>
                    Use of the Service is subject to our&nbsp;
                    <Link href="https://getborrowed.com/privacy-policy" target="_blank" rel="noopener">
                        [Privacy Policy]
                    </Link>
                    . We may send transactional and promotional emails or texts. You may opt out of promotional communications at any time.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    12. Mobile Applications
                </Typography>
                <Typography paragraph>
                    Use of our mobile apps is subject to these Terms and the rules of your app store (Apple/Google). You may incur carrier charges.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    13. Termination
                </Typography>
                <Typography paragraph>
                    We may terminate or suspend your access for violations of these Terms or at our discretion. You are responsible for outstanding payments or obligations.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    14. Limitation of Liability
                </Typography>
                <Typography paragraph>
                    TO THE MAXIMUM EXTENT ALLOWED BY LAW, BORROWED AND ITS AFFILIATES ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES. OUR TOTAL LIABILITY IS LIMITED TO $100 OR THE AMOUNT YOU PAID US IN THE LAST 12 MONTHS, WHICHEVER IS GREATER.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    15. Governing Law and Dispute Resolution
                </Typography>
                <Typography paragraph>
                    These Terms are governed by the laws of the State of Delaware, without regard to its conflict of laws principles. You agree to resolve disputes through binding arbitration administered by JAMS in New Jersey, unless both parties agree to an alternate location. No class actions or jury trials are permitted.
                </Typography>
                <Typography paragraph>
                    If arbitration is deemed unenforceable, any legal action must be brought in the state or federal courts located in New Jersey, and you consent to their exclusive jurisdiction and venue.
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom>
                    16. Miscellaneous
                </Typography>
                <Typography paragraph>
                    Modifications: We may change these Terms at any time. Material changes will be posted and emailed when possible.
                </Typography>
                <Typography paragraph>
                    Entire Agreement: These Terms and our Privacy Policy constitute the full agreement between you and Borrowed.
                </Typography>
                <Typography paragraph>
                    Assignment: You may not assign rights under these Terms. Borrowed may assign freely.
                </Typography>
                <Typography paragraph>
                    Contact: Email us at&nbsp;
                    <Link href="mailto:info@getborrowed.com">info@getborrowed.com</Link>
                    &nbsp;with any questions.
                </Typography>

                <Typography paragraph align="center">
                    © Borrowed, Inc. All rights reserved. [Last Updated: March 12, 2025]
                </Typography>
            </StyledPaper>
        </Container>
    );
}

export default TermsOfService;
